// src/components/Finanzas/MisRelacionesGastos.jsx
// Listado completo de relaciones de gastos del usuario (TODOS los estados,
// incluyendo BORRADOR y CANCELADA). Reusa el patron accordion + lazy load
// de MiHistorialBolsillo.jsx, pero a diferencia de aquel:
//   - Muestra TODAS las relaciones del responsable (no solo cerradas).
//   - En el header del item BORRADOR aparece el boton "Continuar" que
//     navega a /RevisionCierre/:id para retomar el cierre.
//   - Body usa el componente compartido RelacionDetalleContenido.

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container, Row, Col, Card, Accordion, Badge, Alert, Spinner, Button
} from 'react-bootstrap';
import { FaFileInvoiceDollar, FaArrowLeft, FaFilePdf } from 'react-icons/fa';
import { Alertassw } from '../Alertas/Alertassw';
import {
  listarRelaciones,
  listarGastos,
  listarEntregas,
  listarTraslados
} from '../../api/finanzas';
import { useAuth } from '../../hooks/useAuth';
import { extractError } from './utils/extractError';
import RelacionDetalleContenido from './RelacionDetalleContenido';
import { descargarPdfRelacionLocal } from './utils/descargarPdfHelper';
import { formatoFechaColombia } from '../../utils/fechasColombia';

const formatoCOP = (n) => `$${Number(n || 0).toLocaleString('es-CO')}`;

const ESTADO_BADGE = {
  BORRADOR: 'warning',
  APROBADA: 'info',
  SINCRONIZANDO: 'info',
  SINCRONIZADA: 'success',
  SYNC_ERROR: 'danger',
  CANCELADA: 'secondary'
};

const MisRelacionesGastos = () => {
  const { userData } = useAuth();
  const navigate = useNavigate();
  const [relaciones, setRelaciones] = useState([]);
  // Cache por relacionId: { loaded, loading, gastos, entregas, traslados, error, bolsilloId }
  const [detalles, setDetalles] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cargar = async () => {
    setLoading(true);
    try {
      const resp = await listarRelaciones({
        conditions: { responsable: userData._id, activo: true },
        sort: { createdAt: -1 }
      });
      if (resp?.status === 200) setRelaciones(resp.data.data || []);
    } catch (err) {
      console.error('Error cargar relaciones:', err);
      Alertassw('error', 'Error', extractError(err, 'No se pudieron cargar las relaciones'));
    } finally {
      setLoading(false);
    }
  };

  // Lazy load del detalle al abrir cada accordion. Reusa el patron de Historial.
  const handleAccordionSelect = (eventKey) => {
    if (!eventKey) return;
    const relacionId = eventKey;
    if (detalles[relacionId]?.loaded || detalles[relacionId]?.loading) return;
    const relacion = relaciones.find(r => r._id === relacionId);
    if (!relacion) return;
    cargarDetalle(relacion);
  };

  const cargarDetalle = async (relacion) => {
    const relacionId = relacion._id;
    setDetalles(prev => ({ ...prev, [relacionId]: { loading: true } }));
    try {
      const gastosIds    = relacion.gastosIncluidos    || [];
      const entregasIds  = relacion.entregasIncluidas  || [];
      const trasladosIds = relacion.trasladosIncluidos || [];

      const [gastosResp, entregasResp, trasladosResp] = await Promise.all([
        gastosIds.length
          ? listarGastos({ conditions: { _id: { $in: gastosIds } }, sort: { fecha: -1 } })
          : Promise.resolve({ status: 200, data: { data: [] } }),
        entregasIds.length
          ? listarEntregas({ conditions: { _id: { $in: entregasIds } }, sort: { fecha: -1 } })
          : Promise.resolve({ status: 200, data: { data: [] } }),
        trasladosIds.length
          ? listarTraslados({ conditions: { _id: { $in: trasladosIds } }, sort: { fecha: -1 } })
          : Promise.resolve({ status: 200, data: { data: [] } })
      ]);

      const bolsilloId = String(relacion.bolsillo?._id || relacion.bolsillo);
      setDetalles(prev => ({
        ...prev,
        [relacionId]: {
          loaded: true,
          loading: false,
          bolsilloId,
          gastos:    gastosResp?.data?.data    || [],
          entregas:  entregasResp?.data?.data  || [],
          traslados: trasladosResp?.data?.data || []
        }
      }));
    } catch (err) {
      console.error('Error cargar detalle relacion:', err);
      setDetalles(prev => ({
        ...prev,
        [relacionId]: { loading: false, error: extractError(err, 'No se pudo cargar el detalle') }
      }));
    }
  };

  if (loading) {
    return (
      <Container fluid className="py-4 w-100">
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Cargando relaciones...</p>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4 w-100">
      <Row className="mb-4 align-items-center">
        <Col>
          <h2 className="text-primary fw-bold mb-1">
            <FaFileInvoiceDollar className="me-2" />
            Mis Relaciones de Gastos
          </h2>
          <p className="text-muted mb-0">
            Todas tus relaciones (borradores, aprobadas, sincronizadas, canceladas).
          </p>
        </Col>
        <Col xs="auto">
          <Button variant="outline-secondary" onClick={() => navigate('/MiBolsa')}>
            <FaArrowLeft className="me-1" /> Volver a Mi Bolsa
          </Button>
        </Col>
      </Row>

      {relaciones.length === 0 ? (
        <Alert variant="info" className="text-center">
          <FaFileInvoiceDollar size={40} className="mb-2 text-primary" />
          <h5 className="mb-1">Aún no tienes relaciones de gastos</h5>
          <p className="mb-0 small">
            Ve a <a href="/MiBolsa" className="alert-link">Mi Bolsa</a> para iniciar un cierre.
          </p>
        </Alert>
      ) : (
        <Card className="shadow-sm w-100">
          <Card.Body className="w-100">
            <Accordion onSelect={handleAccordionSelect}>
              {relaciones.map(r => {
                const detalle = detalles[r._id];
                const esBorrador = r.estado === 'BORRADOR';
                const fechaPrincipal =
                  r.fechaAprobacion ||
                  r.fechaCierre ||
                  r.fechaInicio ||
                  r.createdAt;

                return (
                  <Accordion.Item eventKey={r._id} key={r._id}>
                    <Accordion.Header>
                      <div className="d-flex justify-content-between w-100 me-3 align-items-center">
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                          <strong>{r.consecutivo}</strong>
                          <Badge bg={ESTADO_BADGE[r.estado] || 'secondary'}>{r.estado}</Badge>
                          {r.tipo === 'CORRECCION' && (
                            <Badge bg="dark">CORRECCIÓN</Badge>
                          )}
                          {esBorrador && (
                            <Badge
                              bg="warning"
                              text="dark"
                              role="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/RevisionCierre/${r._id}`);
                              }}
                              style={{ cursor: 'pointer' }}
                              title="Retomar cierre en borrador"
                            >
                              Continuar →
                            </Badge>
                          )}
                        </div>
                        <div className="d-flex align-items-center gap-3">
                          <small className="text-muted">
                            {fechaPrincipal ? formatoFechaColombia(fechaPrincipal) : '—'}
                          </small>
                          <strong className="text-primary">{formatoCOP(r.montoTotalGastos)}</strong>
                        </div>
                      </div>
                    </Accordion.Header>
                    <Accordion.Body>
                      <RelacionDetalleContenido
                        relacion={r}
                        gastos={detalle?.gastos || []}
                        entregas={detalle?.entregas || []}
                        traslados={detalle?.traslados || []}
                        bolsilloId={detalle?.bolsilloId}
                        cargandoDetalle={detalle?.loading}
                        errorDetalle={detalle?.error}
                        onReintentar={() => cargarDetalle(r)}
                      />

                      <div className="mt-3 d-flex gap-2 flex-wrap">
                        {esBorrador ? (
                          <Button variant="outline-warning" size="sm"
                            onClick={() => navigate(`/RevisionCierre/${r._id}`)}>
                            Continuar cierre en BORRADOR →
                          </Button>
                        ) : (
                          <Button variant="outline-primary" size="sm"
                            onClick={() => navigate(`/DetalleRelacion/${r._id}`)}>
                            Ver detalle completo →
                          </Button>
                        )}
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            descargarPdfRelacionLocal({ relacion: r });
                          }}
                        >
                          <FaFilePdf className="me-1" /> Descargar PDF
                        </Button>
                      </div>
                    </Accordion.Body>
                  </Accordion.Item>
                );
              })}
            </Accordion>
          </Card.Body>
        </Card>
      )}
    </Container>
  );
};

export default MisRelacionesGastos;
